// src/pages/super-admin/SuperAdminDashboard.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  AcademicCapIcon,
  ArrowRightIcon,
  BuildingOffice2Icon,
  CheckCircleIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import apiClient from "../../api/client";
import { useAuth } from "../../context/AuthContext";
import { FireLoader } from "../../components/Loader";
import { showError } from "../../utils/alert";

const EMPTY = {
  summary: {
    total_schools: 0,
    active_schools: 0,
    total_students: 0,
    total_teachers: 0,
  },
  schools: [],
};

const number = (value) =>
  new Intl.NumberFormat().format(Number(value || 0));

const schoolName = (school) =>
  school?.school_name || school?.schoolName || school?.name || "School";

const initials = (name) =>
  String(name || "School")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();

// The super-admin API can return either a complete image URL, an uploads
// path, a filename, a logo field, or a badge field. Normalize all of them.
const resolveSchoolImageUrl = (school) => {
  const candidate =
    school?.logo_url ||
    school?.logoUrl ||
    school?.logo ||
    school?.logo_path ||
    school?.school_logo ||
    school?.badge_url ||
    school?.badgeUrl ||
    school?.badge ||
    school?.badge_path ||
    school?.school_badge ||
    school?.image_url ||
    school?.image ||
    school?.profile?.logo_url ||
    school?.profile?.logo ||
    school?.profile?.badge_url ||
    school?.profile?.badge;

  const rawValue =
    typeof candidate === "object"
      ? candidate?.url || candidate?.path || candidate?.location
      : candidate;

  if (!rawValue) return "";

  const value = String(rawValue).trim().replace(/\\/g, "/");
  if (!value) return "";
  if (/^(https?:|data:|blob:)/i.test(value)) return value;

  const baseUrl =
    apiClient?.defaults?.baseURL || process.env.REACT_APP_API_URL || "";

  try {
    const apiOrigin = new URL(
      baseUrl || window.location.origin,
      window.location.origin,
    ).origin;

    if (value.startsWith("/")) return `${apiOrigin}${value}`;
    if (value.includes("/")) {
      return `${apiOrigin}/${value.replace(/^\/+/, "")}`;
    }

    return `${apiOrigin}/uploads/schools/${value}`;
  } catch {
    return value.startsWith("/") ? value : `/${value}`;
  }
};

const SchoolImage = ({ school }) => {
  const imageUrl = resolveSchoolImageUrl(school);
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    setImageFailed(false);
  }, [imageUrl]);

  if (!imageUrl || imageFailed) {
    return (
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary-100 text-sm font-bold text-primary-700">
        {initials(schoolName(school))}
      </div>
    );
  }

  return (
    <img
      src={imageUrl}
      alt={`${schoolName(school)} logo`}
      className="h-11 w-11 shrink-0 rounded-xl border border-slate-200 bg-white object-contain p-1"
      loading="lazy"
      onError={() => setImageFailed(true)}
    />
  );
};

export default function SuperAdminDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);

    try {
      const response = await apiClient.get("/super-admin/dashboard", {
        params: { page: 1, limit: 6 },
      });
      setData(response?.data?.data || EMPTY);
    } catch (error) {
      await showError({
        title: "Unable to Load Dashboard",
        text:
          error?.response?.data?.message ||
          "Platform records could not be loaded.",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const cards = useMemo(
    () => [
      {
        label: "Registered schools",
        value: data.summary.total_schools,
        note: "All platform schools",
        icon: BuildingOffice2Icon,
        style: "bg-blue-50 text-blue-600",
      },
      {
        label: "Active schools",
        value: data.summary.active_schools,
        note: "Currently active",
        icon: CheckCircleIcon,
        style: "bg-emerald-50 text-emerald-600",
      },
      {
        label: "Students on platform",
        value: data.summary.total_students,
        note: "Across all schools",
        icon: UserGroupIcon,
        style: "bg-violet-50 text-violet-600",
      },
      {
        label: "Teachers on platform",
        value: data.summary.total_teachers,
        note: "Across all schools",
        icon: AcademicCapIcon,
        style: "bg-amber-50 text-amber-600",
      },
    ],
    [data.summary],
  );

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <FireLoader />
      </div>
    );
  }

  return (
    <div className="space-y-5 sm:space-y-6">
      <Helmet>
        <title>Dashboard | PawaSchool Super Admin</title>
      </Helmet>

      <section className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-primary-600 to-secondary-600 p-5 text-white shadow-lg sm:p-8">
        <BuildingOffice2Icon className="absolute -right-6 -top-8 h-44 w-44 text-white opacity-10" />
        <div className="relative">
          <p className="text-xs font-semibold uppercase tracking-wider text-white/70">
            Platform overview
          </p>
          <h1 className="mt-2 text-2xl font-bold sm:text-3xl">
            Welcome, {user?.first_name || "Super Admin"}
          </h1>
          <p className="mt-1 text-sm text-white/80">
            Here is what is happening across PawaSchool today.
          </p>
        </div>
      </section>

      <section className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        {cards.map(({ label, value, note, icon: Icon, style }) => (
          <article
            key={label}
            className="rounded-xl border border-slate-200 bg-white p-3 sm:p-5"
          >
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate text-[10px] font-semibold uppercase tracking-wider text-slate-400 sm:text-xs">
                  {label}
                </p>
                <p className="mt-1 text-xl font-bold text-slate-800 sm:text-3xl">
                  {number(value)}
                </p>
              </div>
              <span
                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl sm:h-12 sm:w-12 ${style}`}
              >
                <Icon className="h-5 w-5 sm:h-6 sm:w-6" />
              </span>
            </div>
            <p className="mt-3 border-t border-slate-100 pt-3 text-[10px] text-slate-400 sm:text-xs">
              {note}
            </p>
          </article>
        ))}
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold text-slate-900">
              Recently registered schools
            </h2>
            <p className="text-sm text-slate-400">
              Latest additions to the platform
            </p>
          </div>
          <Link
            to="/super-admin/schools"
            className="inline-flex items-center gap-1.5 text-sm font-bold text-primary-700 hover:text-primary-800"
          >
            View all <ArrowRightIcon className="h-4 w-4" />
          </Link>
        </div>

        <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {data.schools.slice(0, 6).map((school) => (
            <article
              key={school.id}
              className="flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50 p-3"
            >
              <SchoolImage school={school} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-bold text-slate-800">
                  {schoolName(school)}
                </p>
                <p className="truncate text-xs text-slate-400">
                  {school.school_code || `School #${school.id}`}
                </p>
              </div>
              <span
                className={`h-2.5 w-2.5 rounded-full ${
                  school.status === "active"
                    ? "bg-emerald-500"
                    : "bg-slate-300"
                }`}
              />
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}